import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExpenseService } from '@/services/expense.service';
import {
  ExpenseStatus,
  ExpenseStatusLabels,
  ExpenseCategory,
  ExpenseCategoryLabels,
  ExpenseCategoryGroups,
} from '@/models/expense.model';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';

type TabType = 'expenses' | 'reports' | 'pending';
type DatePreset = 'all' | 'today' | 'week' | 'month' | 'quarter' | 'custom';

export const Expenses: React.FC = () => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('expenses');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<ExpenseCategory | 'all'>('all');
  const [datePreset, setDatePreset] = useState<DatePreset>('month');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'status'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  const { data: expenses = [], isLoading: expensesLoading } = useQuery({
    queryKey: ['expenses'],
    queryFn: () => ExpenseService.getAllExpenses(),
    enabled: !!profile,
  });

  const { data: reports = [], isLoading: reportsLoading } = useQuery({
    queryKey: ['expenseReports'],
    queryFn: () => ExpenseService.getAllReports(),
    enabled: !!profile,
  });

  const { data: pendingExpenses = [] } = useQuery({
    queryKey: ['pendingExpenses'],
    queryFn: () => ExpenseService.getPendingApprovalExpenses(),
    enabled: !!profile && activeTab === 'pending',
  });

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: async () => {
      if (!profile) return;
      await ExpenseService.bulkApproveExpenses(
        Array.from(selectedIds),
        profile.id || '',
        profile.fullName || profile.email || ''
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['pendingExpenses'] });
      setSelectedIds(new Set());
      setShowBulkActions(false);
    },
  });

  // Date range calculation
  const dateRange = useMemo(() => {
    const now = new Date();
    switch (datePreset) {
      case 'today':
        return { start: now, end: now };
      case 'week':
        return { start: subDays(now, 7), end: now };
      case 'month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'quarter':
        return { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : null,
          end: customEndDate ? new Date(customEndDate) : null,
        };
      default:
        return { start: null, end: null };
    }
  }, [datePreset, customStartDate, customEndDate]);

  // Filtered and sorted expenses
  const filteredExpenses = useMemo(() => {
    let result = [...expenses];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(e =>
        e.description.toLowerCase().includes(query) ||
        e.merchant?.toLowerCase().includes(query) ||
        e.employeeName.toLowerCase().includes(query) ||
        e.notes?.toLowerCase().includes(query)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(e => e.status === statusFilter);
    }
    
    // Category filter
    if (categoryFilter !== 'all') {
      result = result.filter(e => e.category === categoryFilter);
    }
    
    // Date filter
    if (dateRange.start) {
      result = result.filter(e => e.expenseDate >= dateRange.start!);
    }
    if (dateRange.end) {
      result = result.filter(e => e.expenseDate <= dateRange.end!);
    }
    
    // Sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'date':
          comparison = a.expenseDate.getTime() - b.expenseDate.getTime();
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortDir === 'desc' ? -comparison : comparison;
    });
    
    return result;
  }, [expenses, searchQuery, statusFilter, categoryFilter, dateRange, sortBy, sortDir]);

  // Filtered reports
  const filteredReports = useMemo(() => {
    let result = [...reports];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r =>
        r.title.toLowerCase().includes(query) ||
        r.reportNumber.toLowerCase().includes(query) ||
        r.employeeName.toLowerCase().includes(query)
      );
    }
    
    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }
    
    return result;
  }, [reports, searchQuery, statusFilter]);

  // Statistics
  const stats = useMemo(() => {
    const totalAmount = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
    const pendingCount = expenses.filter(e => 
      e.status === ExpenseStatus.submitted || e.status === ExpenseStatus.pendingApproval
    ).length;
    const pendingAmount = expenses
      .filter(e => e.status === ExpenseStatus.submitted || e.status === ExpenseStatus.pendingApproval)
      .reduce((sum, e) => sum + e.amount, 0);
    const approvedThisMonth = expenses.filter(e => {
      const now = new Date();
      return e.status === ExpenseStatus.approved &&
        e.expenseDate.getMonth() === now.getMonth() &&
        e.expenseDate.getFullYear() === now.getFullYear();
    }).reduce((sum, e) => sum + e.amount, 0);
    
    return {
      totalAmount,
      totalCount: filteredExpenses.length,
      pendingCount,
      pendingAmount,
      approvedThisMonth,
      reportsCount: reports.length,
    };
  }, [filteredExpenses, expenses, reports]);

  const getStatusColor = (status: ExpenseStatus) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      pending_payment: 'bg-purple-100 text-purple-800',
      paid: 'bg-emerald-100 text-emerald-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const selectAll = () => {
    if (selectedIds.size === filteredExpenses.length) {
      setSelectedIds(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedIds(new Set(filteredExpenses.map(e => e.id)));
      setShowBulkActions(true);
    }
  };

  const handleExport = () => {
    const csv = ExpenseService.exportExpensesToCSV(filteredExpenses);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setDatePreset('month');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Expense Management</h1>
            <p className="mt-1 text-sm text-gray-500">Track, manage, and analyze employee expenses</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/expenses/analytics"
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Analytics
            </Link>
            <Link
              to="/expenses/billing-conversion"
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Convert Billing
            </Link>
            <Link
              to="/expenses/reports/new"
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              New Report
            </Link>
            <Link
              to="/expenses/new"
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              + Add Expense
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Total Expenses</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">
            ${stats.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-xs text-gray-500">{stats.totalCount} expenses (filtered)</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Pending Approval</div>
          <div className="mt-1 text-2xl font-bold text-yellow-600">
            ${stats.pendingAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-xs text-gray-500">{stats.pendingCount} expenses</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Approved This Month</div>
          <div className="mt-1 text-2xl font-bold text-green-600">
            ${stats.approvedThisMonth.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm font-medium text-gray-500">Expense Reports</div>
          <div className="mt-1 text-2xl font-bold text-blue-600">{stats.reportsCount}</div>
          <div className="text-xs text-gray-500">total reports</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex-1 min-w-64">
            <div className="relative">
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ExpenseStatus | 'all')}
            className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            {Object.entries(ExpenseStatusLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as ExpenseCategory | 'all')}
            className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Categories</option>
            {Object.entries(ExpenseCategoryGroups).map(([group, categories]) => (
              <optgroup key={group} label={group}>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{ExpenseCategoryLabels[cat]}</option>
                ))}
              </optgroup>
            ))}
          </select>

          {/* Date Filter */}
          <select
            value={datePreset}
            onChange={(e) => setDatePreset(e.target.value as DatePreset)}
            className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="custom">Custom Range</option>
          </select>

          {datePreset === 'custom' && (
            <>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </>
          )}

          {/* Clear Filters */}
          {(searchQuery || statusFilter !== 'all' || categoryFilter !== 'all' || datePreset !== 'month') && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Clear filters
            </button>
          )}

          {/* Export */}
          <button
            onClick={handleExport}
            className="ml-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'expenses' as const, name: 'All Expenses', count: filteredExpenses.length },
            { id: 'reports' as const, name: 'Reports', count: filteredReports.length },
            { id: 'pending' as const, name: 'Pending Approval', count: stats.pendingCount },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs ${
                  tab.id === 'pending' && tab.count > 0 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Bulk Actions Bar */}
      {showBulkActions && activeTab !== 'reports' && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 flex items-center justify-between">
          <span className="text-sm text-blue-700">
            {selectedIds.size} expense{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-3">
            <button
              onClick={() => bulkApproveMutation.mutate()}
              disabled={bulkApproveMutation.isPending}
              className="px-3 py-1 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
            >
              {bulkApproveMutation.isPending ? 'Approving...' : 'Approve Selected'}
            </button>
            <button
              onClick={() => {
                setSelectedIds(new Set());
                setShowBulkActions(false);
              }}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {activeTab === 'expenses' && (
        <div>
          {expensesLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading expenses...</p>
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No expenses found</h3>
              <p className="mt-2 text-gray-500">
                {searchQuery || statusFilter !== 'all' || categoryFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by adding your first expense'}
              </p>
              <Link
                to="/expenses/new"
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Add Expense
              </Link>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="col-span-1 flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === filteredExpenses.length && filteredExpenses.length > 0}
                    onChange={selectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <div className="col-span-4">Description</div>
                <div className="col-span-2">Category</div>
                <div 
                  className="col-span-2 cursor-pointer hover:text-gray-700 flex items-center gap-1"
                  onClick={() => {
                    if (sortBy === 'date') {
                      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy('date');
                      setSortDir('desc');
                    }
                  }}
                >
                  Date
                  {sortBy === 'date' && (
                    <span>{sortDir === 'desc' ? '↓' : '↑'}</span>
                  )}
                </div>
                <div 
                  className="col-span-2 text-right cursor-pointer hover:text-gray-700 flex items-center justify-end gap-1"
                  onClick={() => {
                    if (sortBy === 'amount') {
                      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy('amount');
                      setSortDir('desc');
                    }
                  }}
                >
                  Amount
                  {sortBy === 'amount' && (
                    <span>{sortDir === 'desc' ? '↓' : '↑'}</span>
                  )}
                </div>
                <div className="col-span-1">Status</div>
              </div>

              {/* Table Body */}
              <ul className="divide-y divide-gray-200">
                {filteredExpenses.map((expense) => (
                  <li key={expense.id} className="hover:bg-gray-50">
                    <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                      <div className="col-span-1">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(expense.id)}
                          onChange={() => toggleSelection(expense.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="col-span-4">
                        <Link to={`/expenses/${expense.id}`} className="block">
                          <p className="text-sm font-medium text-gray-900 hover:text-blue-600">
                            {expense.description}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {expense.employeeName}
                            {expense.merchant && ` • ${expense.merchant}`}
                          </p>
                        </Link>
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm text-gray-600">
                          {ExpenseCategoryLabels[expense.category]}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm text-gray-600">
                          {format(expense.expenseDate, 'MMM d, yyyy')}
                        </span>
                      </div>
                      <div className="col-span-2 text-right">
                        <span className="text-sm font-medium text-gray-900">
                          ${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="col-span-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(expense.status)}`}>
                          {ExpenseStatusLabels[expense.status]}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {activeTab === 'reports' && (
        <div>
          {reportsLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading reports...</p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No expense reports</h3>
              <p className="mt-2 text-gray-500">Create a report to group and submit expenses together</p>
              <Link
                to="/expenses/reports/new"
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Create Report
              </Link>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <ul className="divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <li key={report.id}>
                    <Link to={`/expenses/reports/${report.id}`} className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <p className="text-sm font-medium text-gray-900">{report.title}</p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(report.status)}`}>
                              {ExpenseStatusLabels[report.status]}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {report.reportNumber} • {report.employeeName} • 
                            {format(report.startDate, 'MMM d')} - {format(report.endDate, 'MMM d, yyyy')} •
                            {report.expenseIds?.length || 0} expenses
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-sm font-medium text-gray-900">
                            ${report.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                          {report.reimbursableAmount > 0 && (
                            <p className="text-xs text-green-600">
                              ${report.reimbursableAmount.toFixed(2)} reimbursable
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {activeTab === 'pending' && (
        <div>
          {pendingExpenses.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">All caught up!</h3>
              <p className="mt-2 text-gray-500">No expenses pending approval</p>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b bg-yellow-50">
                <p className="text-sm text-yellow-800">
                  <strong>{pendingExpenses.length}</strong> expense{pendingExpenses.length !== 1 ? 's' : ''} pending approval
                </p>
              </div>
              <ul className="divide-y divide-gray-200">
                {pendingExpenses.map((expense) => (
                  <li key={expense.id}>
                    <Link to={`/expenses/${expense.id}`} className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{expense.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {expense.employeeName} • {ExpenseCategoryLabels[expense.category]} • 
                            {format(expense.expenseDate, 'MMM d, yyyy')}
                            {expense.merchant && ` • ${expense.merchant}`}
                          </p>
                          {expense.submittedAt && (
                            <p className="text-xs text-gray-400 mt-1">
                              Submitted {format(expense.submittedAt, 'MMM d, yyyy')}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-sm font-medium text-gray-900">
                            ${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(expense.status)}`}>
                            {ExpenseStatusLabels[expense.status]}
                          </span>
                        </div>
                      </div>
                      {expense.policyViolations && expense.policyViolations.length > 0 && (
                        <div className="mt-2 flex items-center text-xs text-yellow-600">
                          <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {expense.policyViolations.length} policy violation{expense.policyViolations.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
